import { combineReducers } from 'redux';
import reduceReducers from 'reduce-reducers';
import { reducer as formReducer } from 'redux-form';
import { routerReducer } from 'react-router-redux';
import { all } from 'redux-saga/effects';

import routerParams from './routerParams';
import account, { accountSaga } from './account';
import patients, { patientsSaga } from './patients';
import patientsVitals, { patientsVitalsSaga } from './patientsVitals';
import patientsNotes, { patientsNotesSaga } from './patientsNotes';
import patientsGlobalNotes, { patientsGlobalNotesSaga } from './patientsGlobalNotes';
import patientsInsurance, { patientsInsuranceSaga } from './patientsInsurance';
import patientsCreditCards, { patientsCreditCardsSaga } from './patientsCreditCards';
import patientsDocuments, { patientsDocumentsSaga } from './patientsDocuments';
import inventory, { inventorySaga } from './inventory';
import inventoryStockLine, { inventoryStockLineSaga } from './inventoryStockLine';
import inventoryStockLineHistory, { inventoryStockLineHistorySaga } from './inventoryStockLineHistory';
import business, { businessSaga } from './business';
import facilities, { facilitiesSaga } from './facilities';
import formControl from './formControl';
import orders, { ordersSaga } from './orders';
import ordersLines, { ordersLinesSaga } from './ordersLines';
import ordersLinesDocuments, { ordersLinesDocumentsSaga } from './ordersLinesDocuments';
import ordersLinesClaims, { ordersLinesClaimsSaga } from './ordersLinesClaims';
import ordersNotes, { ordersNotesSaga } from './ordersNotes';
import archiveOrders, { archiveOrdersSaga } from './archiveOrders';
import connectedOrders, { connectedOrdersSaga } from './connectedOrders';
import users, { usersSaga } from './users';
import referralSources, { referralSourcesSaga } from './referralSources';
import salesRepresentatives, { salesRepresentativesSaga } from './salesRepresentatives';
import prescribers, { prescribersSaga } from './prescribers';
import reports, { reportsSaga } from './reports';
import reportStructures, { reportStructuresSaga } from './reportStructures';
import defaultReportStructure, { defaultReportStructureSaga } from './defaultReportStructure';
import templates, { templatesSaga } from './templates';
import dataPortability, { dataPortabilitySaga } from './dataPortability';
import eventLog, { eventLogSaga } from './eventLog';
import notifications, { notificationsSaga } from './notifications';
import ordersStatistics, { ordersStatisticsSaga } from './ordersStatistics';
import eraStatistics, { eraStatisticsSaga } from './eraStatistics';
import totalStatistics, { totalStatisticsSaga } from './totalStatistics';
import vendors, { vendorsSaga } from './vendors';
import systemSettings, { systemSettingsSaga } from './systemSettings';
import prescribersRegistry, { prescribersRegistrySaga } from './prescribersRegistry';
import { emailsSaga } from './emails';
import recoverOrder from './recoverOrder';
import hcpcsCodes, { hcpcsCodesSaga } from './hcpcsCodes';
import diagnoses, { diagnosesSaga } from './diagnoses';
import insurances, { insurancesSaga } from './insurances';
import roles, { rolesSaga } from './roles';
import accessPermissions, { accessPermissionsSaga } from './accessPermissions';
import appInfo from './appInfo';
import noteCategories, { noteCategoriesSaga } from './noteCategories';
import inventoryCategories, { inventoryCategoriesSaga } from './inventoryCategories';
import inventoryConditions, { inventoryConditionsSaga } from './inventoryConditions';
import inventoryWarehouses, { inventoryWarehousesSaga } from './inventoryWarehouses';
import documentCategories, { documentCategoriesSaga } from './documentCategories';
import claims, { claimsSaga } from './claims';
import patientsMessages, { patientsMessagesSaga } from './patientsMessages';
import patientsMessagesTemplates, { patientsMessagesTemplatesSaga } from './patientsMessagesTemplates';
import tabID from './tabID';
import workflowStatuses, { workflowStatusesSaga } from './workflowStatuses';
import userNotifications, { userNotificationsSaga } from './userNotifications';
import userNotificationSettings, { userNotificationSettingsSaga } from './userNotificationSettings';
import userNotificationsHistory, { userNotificationsHistorySaga } from './userNotificationsHistory';
import claimStatuses, { claimStatusesSaga } from './claimStatuses';
import claimAdjustmentCodes, { claimAdjustmentCodesSaga } from './claimAdjustmentCodes';
import eraActions, { eraActionsSaga } from './eraActions';
import paymentPlans, { paymentPlansSaga } from './paymentPlans';
import paymentEvents, { paymentEventsSaga } from './paymentEvents';

export default combineReducers({
  router: routerReducer,
  routerParams,
  form: formReducer,
  account,
  patients: reduceReducers(
    patients,
    patientsVitals,
    patientsNotes,
    patientsGlobalNotes,
    patientsInsurance,
    patientsCreditCards,
    patientsDocuments,
  ),
  inventory: reduceReducers(
    inventory,
    inventoryStockLine,
  ),
  inventoryStockLineHistory,
  business,
  facilities,
  formControl,
  orders: reduceReducers(
    orders,
    ordersLines,
    ordersLinesDocuments,
    ordersNotes,
    ordersLinesClaims,
  ),
  archiveOrders,
  connectedOrders,
  users,
  referralSources,
  salesRepresentatives,
  prescribers,
  reports,
  reportStructures,
  defaultReportStructure,
  templates,
  dataPortability,
  eventLog,
  notifications,
  ordersStatistics,
  eraStatistics,
  totalStatistics,
  vendors,
  systemSettings,
  prescribersRegistry,
  recoverOrder,
  hcpcsCodes,
  diagnoses,
  insurances,
  roles,
  accessPermissions,
  appInfo,
  noteCategories,
  inventoryCategories,
  inventoryConditions,
  inventoryWarehouses,
  documentCategories,
  claims,
  patientsMessages,
  patientsMessagesTemplates,
  tabID,
  workflowStatuses,
  userNotifications,
  userNotificationSettings,
  userNotificationsHistory,
  claimStatuses,
  claimAdjustmentCodes,
  paymentPlans,
  paymentEvents,
  eraActions,
});

export function* rootSaga() {
  yield all([
    accountSaga(),
    patientsSaga(),
    patientsVitalsSaga(),
    patientsNotesSaga(),
    patientsGlobalNotesSaga(),
    patientsInsuranceSaga(),
    patientsCreditCardsSaga(),
    patientsDocumentsSaga(),
    inventorySaga(),
    inventoryStockLineSaga(),
    inventoryStockLineHistorySaga(),
    businessSaga(),
    facilitiesSaga(),
    usersSaga(),
    referralSourcesSaga(),
    salesRepresentativesSaga(),
    prescribersSaga(),
    reportsSaga(),
    reportStructuresSaga(),
    defaultReportStructureSaga(),
    ordersSaga(),
    ordersLinesSaga(),
    ordersLinesDocumentsSaga(),
    ordersLinesClaimsSaga(),
    ordersNotesSaga(),
    archiveOrdersSaga(),
    connectedOrdersSaga(),
    templatesSaga(),
    dataPortabilitySaga(),
    eventLogSaga(),
    vendorsSaga(),
    ordersStatisticsSaga(),
    eraStatisticsSaga(),
    totalStatisticsSaga(),
    systemSettingsSaga(),
    prescribersRegistrySaga(),
    emailsSaga(),
    notificationsSaga(),
    hcpcsCodesSaga(),
    diagnosesSaga(),
    insurancesSaga(),
    rolesSaga(),
    accessPermissionsSaga(),
    noteCategoriesSaga(),
    inventoryCategoriesSaga(),
    inventoryConditionsSaga(),
    inventoryWarehousesSaga(),
    documentCategoriesSaga(),
    claimsSaga(),
    workflowStatusesSaga(),
    patientsMessagesSaga(),
    patientsMessagesTemplatesSaga(),
    userNotificationsSaga(),
    userNotificationSettingsSaga(),
    userNotificationsHistorySaga(),
    claimStatusesSaga(),
    claimAdjustmentCodesSaga(),
    paymentPlansSaga(),
    paymentEventsSaga(),
    eraActionsSaga(),
  ]);
}



// WEBPACK FOOTER //
// ./src/redux/modules/index.js